import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { diffWords, type Change } from 'diff';
import { Added, DiffColumn, DiffColumns, DiffLabel, DiffText, Removed } from './PromptDiff.styled';

const CONTEXT_ABOVE_CHANGE_PX = 48;

type DiffSide = 'before' | 'after';

interface PromptDiffProps {
  readonly before: string;
  readonly after: string;
}

interface SideTextProps {
  readonly parts: readonly Change[];
  readonly side: DiffSide;
}

/** One side of the diff: the words both share, plus the ones only this side has, marked. */
const SideText: React.FC<SideTextProps> = ({ parts, side }) => {
  const boxRef = useRef<HTMLParagraphElement>(null);
  const firstChangeRef = useRef<HTMLModElement>(null);
  const firstChange = parts.findIndex((part) => (side === 'before' ? part.removed : part.added));

  useLayoutEffect(() => {
    const box = boxRef.current;
    const change = firstChangeRef.current;
    if (box && change) box.scrollTop = Math.max(0, change.offsetTop - CONTEXT_ABOVE_CHANGE_PX);
  }, [parts]);

  return (
    <DiffText ref={boxRef} data-testid={`prompt-diff__${side}`}>
      {parts.map((part, position) => {
        const otherSideOnly = side === 'before' ? part.added : part.removed;
        if (otherSideOnly) return null;
        const key = `${side}-${position}`;
        const ref = position === firstChange ? firstChangeRef : undefined;
        if (part.removed) return <Removed key={key} ref={ref}>{part.value}</Removed>;
        if (part.added) return <Added key={key} ref={ref}>{part.value}</Added>;
        return <span key={key}>{part.value}</span>;
      })}
    </DiffText>
  );
};

/** The current prompt and the suggested one side by side, each marking the words the other lacks. */
const PromptDiff: React.FC<PromptDiffProps> = ({ before, after }) => {
  const intl = useIntl();
  const parts = useMemo(() => diffWords(before, after), [before, after]);

  return (
    <DiffColumns data-testid="prompt-diff">
      <DiffColumn>
        <DiffLabel>
          {intl.formatMessage({ id: 'apps.builder.review.current', defaultMessage: 'Your prompt' })}
        </DiffLabel>
        <SideText parts={parts} side="before" />
      </DiffColumn>
      <DiffColumn>
        <DiffLabel>
          {intl.formatMessage({ id: 'apps.builder.review.suggested', defaultMessage: 'Suggested' })}
        </DiffLabel>
        <SideText parts={parts} side="after" />
      </DiffColumn>
    </DiffColumns>
  );
};

export default PromptDiff;
